import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { useConnectors, useSaveToken } from './useConnectors'
import { CONNECTOR_LABELS, isResolvableByTypeAlone } from '@/components/projects/connectors/connectorConstants'
import { describeTokenSaveError } from '@/lib/connectorTokenErrors'
import type { ConnectorType } from '@/types'

interface UseReconnectConnectorOptions {
  projectId: string
  onSuccess?: (connectorType: ConnectorType) => void
}

interface ReconnectState {
  tokenDialogType: ConnectorType | null
  tokenDialogOpen: boolean
  tokenInlineError: string | null
}

export function useReconnectConnector({ projectId, onSuccess }: UseReconnectConnectorOptions) {
  const { data: connectors = [] } = useConnectors(projectId)
  const saveToken = useSaveToken(projectId)

  const [state, setState] = useState<ReconnectState>({
    tokenDialogType: null,
    tokenDialogOpen: false,
    tokenInlineError: null,
  })

  const findExistingConnector = useCallback(
    (type: ConnectorType) => connectors.find((c) => c.type === type && c.isEnabled),
    [connectors],
  )

  const canReconnect = useCallback(
    (type: ConnectorType) => findExistingConnector(type) !== undefined && isResolvableByTypeAlone(type, connectors),
    [connectors, findExistingConnector],
  )

  const reconnect = useCallback((type: ConnectorType) => {
    setState({
      tokenDialogType: type,
      tokenDialogOpen: true,
      tokenInlineError: null,
    })
  }, [])

  const existingConnector = state.tokenDialogType
    ? findExistingConnector(state.tokenDialogType)
    : undefined

  const saveTokenFlow = useCallback(
    async (connectionName: string, token: string, email?: string, site?: string) => {
      const type = state.tokenDialogType
      if (!type) return

      setState((s) => ({ ...s, tokenInlineError: null }))
      try {
        await saveToken.mutateAsync({
          connectorType: type,
          data: { connectorId: existingConnector?.id, connectionName, token, email, site },
        })
        toast.success(`${CONNECTOR_LABELS[type]} reconnected successfully`)
        setState({
          tokenDialogType: null,
          tokenDialogOpen: false,
          tokenInlineError: null,
        })
        if (onSuccess) onSuccess(type)
      } catch (error) {
        const message = describeTokenSaveError(error)

        if (message) {
          setState((s) => ({ ...s, tokenInlineError: message }))
        } else {
          toast.error(`Failed to reconnect ${CONNECTOR_LABELS[type]}`)
        }
      }
    },
    [saveToken, state.tokenDialogType, existingConnector, onSuccess],
  )

  const closeTokenDialog = useCallback((open: boolean) => {
    setState((s) => ({
      ...s,
      tokenDialogOpen: open,
      tokenInlineError: open ? s.tokenInlineError : null,
    }))
  }, [])

  return {
    reconnect,
    canReconnect,
    isReconnecting: saveToken.isPending,
    tokenDialog: {
      projectId,
      connectorType: state.tokenDialogType,
      open: state.tokenDialogOpen,
      onOpenChange: closeTokenDialog,
      onSave: saveTokenFlow,
      isSaving: saveToken.isPending,
      inlineError: state.tokenInlineError,
      reconnectConnectionName: existingConnector?.connectionName,
    },
  }
}
